import styled, { css } from 'styled-components';
import type { Theme } from '@/styles/theme';
import { headingFont } from '@/styles/mixins';

export type StateTone = 'neutral' | 'gold' | 'warning' | 'danger' | 'success' | 'navy';

const toneTokens = (theme: Theme, tone: StateTone) => {
  switch (tone) {
    case 'gold':
      return { glyphBg: 'rgba(212,168,67,0.12)', glyphFg: theme.colors.warningText, glyphBorder: 'rgba(212,168,67,0.3)' };
    case 'warning':
      return { glyphBg: 'rgba(217,119,6,0.10)', glyphFg: theme.colors.warningText, glyphBorder: 'rgba(217,119,6,0.25)' };
    case 'danger':
      return { glyphBg: theme.colors.dangerBg, glyphFg: theme.colors.dangerText, glyphBorder: theme.colors.dangerBorder };
    case 'success':
      return { glyphBg: theme.colors.successBg, glyphFg: theme.colors.successText, glyphBorder: theme.colors.successBorder };
    case 'navy':
      return { glyphBg: 'rgba(212,168,67,0.15)', glyphFg: theme.colors.accent, glyphBorder: 'rgba(212,168,67,0.3)' };
    case 'neutral':
    default:
      return { glyphBg: theme.colors.gray50, glyphFg: theme.colors.gray400, glyphBorder: theme.colors.gray100 };
  }
};

export const PageRoot = styled.main<{ $dark?: boolean; $warn?: boolean }>`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme, $dark, $warn }) =>
    $dark ? theme.colors.primary : $warn ? theme.colors.warningBg : theme.colors.bgBody};
`;

export const Frame = styled.div<{ $dark?: boolean; $warn?: boolean }>`
  background: ${({ theme, $dark }) => ($dark ? theme.colors.primary : theme.colors.bgSurface)};
  border: 1px solid
    ${({ theme, $dark, $warn }) =>
      $dark ? theme.colors.primary : $warn ? theme.colors.warningBorder : theme.colors.gray200};
  border-radius: ${({ theme }) => theme.radii.xl};
  box-shadow: ${({ theme, $dark }) => ($dark ? 'none' : theme.shadows.sm)};
  padding: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[8]}`};
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: ${({ theme, $dark }) => ($dark ? theme.colors.gray100 : theme.colors.textPrimary)};

  ${({ $dark }) =>
    $dark &&
    css`
      [data-surface='navy'];
    `}
`;

export const Glyph = styled.div<{ $tone: StateTone; $size?: 'md' | 'lg' }>`
  ${({ $tone, theme }) => {
    const c = toneTokens(theme, $tone);
    return css`
      background: ${c.glyphBg};
      color: ${c.glyphFg};
      border: 1px solid ${c.glyphBorder};
    `;
  }}
  width: ${({ $size }) => ($size === 'lg' ? '88px' : '64px')};
  height: ${({ $size }) => ($size === 'lg' ? '88px' : '64px')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ $size }) => ($size === 'lg' ? '2rem' : '1.5rem')};
`;

export const Title = styled.h1<{ $dark?: boolean }>`
  ${headingFont}
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme, $dark }) => ($dark ? theme.colors.textInverse : theme.colors.primary)};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
  max-width: 360px;
  line-height: ${({ theme }) => theme.lineHeights.tight};
`;

export const Body = styled.p<{ $dark?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme, $dark }) => ($dark ? theme.colors.gray300 : theme.colors.textSecondary)};
  margin: 0 0 ${({ theme }) => theme.spacing[5]};
  max-width: 360px;
  line-height: ${({ theme }) => theme.lineHeights.normal};

  b {
    color: ${({ theme, $dark }) => ($dark ? theme.colors.textInverse : theme.colors.primary)};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
  }
`;

export const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
  justify-content: center;
`;

export const MetaLine = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.gray400};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

export const IncidentId = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.6875rem;
  background: rgba(255, 255, 255, 0.06);
  color: ${({ theme }) => theme.colors.accent};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.radii.sm};
  border: 1px solid rgba(212, 168, 67, 0.3);
  margin-top: ${({ theme }) => theme.spacing[4]};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  b {
    color: ${({ theme }) => theme.colors.textInverse};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
  }
`;
